/**
 * Client-side mirror of what the backend will accept as a BOB50 reference.
 *
 * Mirrored rather than merely awaited because the reference is not an ordinary field: `CID` is
 * `C(10)` in BOB50 and BOBLink upserts by it, so a value that reaches the dossier and collides
 * overwrites another contact's record instead of failing. Telling the accountant while they are
 * still typing is worth the duplication; the backend remains the authority, and the one rule that
 * cannot live here at all — whether the reference is already taken in this company — comes back
 * as a 409.
 *
 * Kept in step with `AdminBobContactDto.bobId` in easybiz-backend. If those decorators change,
 * this and its test change with them.
 */

/** BOB50's `CID` column is `C(10)`. Not a policy — a column width. */
export const MAX_BOB_ID_LENGTH = 10;

const ALLOWED_CHARACTERS = /^[A-Za-z0-9]+$/;
const HAS_A_NON_DIGIT = /\D/;

export type BobIdProblem =
  | 'empty'
  | 'too-long'
  | 'illegal-characters'
  | 'all-digits';

/**
 * What is wrong with this reference, or null when nothing is.
 *
 * @param value - The raw field contents, as typed.
 */
export const bobIdProblem = (
  value: string | undefined | null
): BobIdProblem | null => {
  const candidate = (value ?? '').trim();

  if (!candidate) {
    return 'empty';
  }
  if (candidate.length > MAX_BOB_ID_LENGTH) {
    return 'too-long';
  }
  if (!ALLOWED_CHARACTERS.test(candidate)) {
    return 'illegal-characters';
  }
  if (!HAS_A_NON_DIGIT.test(candidate)) {
    // Accounting software reads an all-digit reference as an account number, which is the one
    // way a syntactically fine reference still causes damage downstream.
    return 'all-digits';
  }
  return null;
};

const MESSAGES: Record<BobIdProblem, string> = {
  empty: 'A BOB50 reference is required',
  'too-long': `At most ${MAX_BOB_ID_LENGTH} characters — BOB50 stores the reference in a 10-character field`,
  'illegal-characters': 'Letters and digits only',
  'all-digits':
    'Add at least one letter — accounting software reads an all-digit reference as an account number',
};

/** The message to show against the field, or null when the reference is acceptable. */
export const bobIdError = (value: string | undefined | null): string | null => {
  const problem = bobIdProblem(value);
  return problem === null ? null : MESSAGES[problem];
};

/**
 * Normalise what was typed into what will be sent.
 *
 * Upper-cased because BOB50 references are upper case by convention and the backend does not
 * rewrite the value — it validates and stores it verbatim, so lower case typed here would stay
 * lower case in the dossier forever.
 */
export const normaliseBobId = (value: string | undefined | null): string =>
  (value ?? '').trim().toUpperCase();
